import { useMessenger } from "@/framework/Messenger/Messenger";
import Api from "@/services/Api";
import { useStore } from "@/Store";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const autoCloseDuration = 5000;
export const allowed_languages = ["en", "no", "nb", "nn", "sv", "ro", "pl", "es", "de", "fr"];

export const useAiFunctions: any = () => {
  const { state, setGeneratedData } = useStore();
  const messenger = useMessenger();
  const { t } = useTranslation("common");
  const selectedUserLanguage = localStorage.getItem("i18nextLng") || "en";
  const previousFieldId = useRef<string | null>(null);
  const previousImageSrc = useRef<string | null>(null);
  const previousDocumentId = useRef<string | null>(null);

  async function generateText() {
    switch (state.generatedData?.functionType) {
      case "altText":
        await generateAltText();
        break;
      case "textFeedback":
        await generateTextFeedback();
        break;
      default:
        messenger.error(
          t("aiFunctions.unsupportedFunctionType", { functionType: state.generatedData?.functionType }),
          autoCloseDuration
        );
        setGeneratedData(null);
    }
  }

  async function generateTextFeedback() {
    try {
      const foundTextBlock = state.generatedData?.originalValue;
      if (!foundTextBlock) {
        messenger.error(t("aiFunctions.noTextBlockFound"), autoCloseDuration);
        return;
      }

      const apiRequest = await Api.postFetchTextFeedback({
        text: foundTextBlock,
        locale: state?.generatedData?.selectedLocale ?? selectedUserLanguage,
        feedbackLocale: selectedUserLanguage,
      }).fetchDirect({});

      if (!!apiRequest) {
        setGeneratedData({
          ...state.generatedData,
          loading: false,
          generatedResponse: apiRequest,
          showGeneratedFeedback: true,
          doGenerate: false,
        });
      } else {
        messenger.error(t("aiFunctions.couldNotGenerateTextFeedback"), autoCloseDuration);
        setGeneratedData(null);
      }
    } catch (error) {
      messenger.error(t("aiFunctions.failedToGenerateTextFeedback"), autoCloseDuration);
      setGeneratedData(null);
    }
  }

  async function generateAltText() {
    const existingAltTextWarning = t("aiFunctions.confirmOverwriteAltText");
    if (
      !!state.generatedData?.originalValue?.[
        state.generatedData?.selectedLocale === "nn" ? "no" : state.generatedData?.selectedLocale ?? ""
      ]
    ) {
      if (!window.confirm(existingAltTextWarning)) {
        setGeneratedData(null);
        return;
      }
    }

    let textToTranslate;

    if (!!state.generatedData?.originalValue) {
      if (
        !!state.generatedData.originalValue["en"] &&
        state.generatedData.originalValue["en"].length > 2 &&
        "en" !== state.generatedData.selectedLocale
      ) {
        textToTranslate = state.generatedData.originalValue["en"];
      } else {
        for (let key in state.generatedData.originalValue) {
          if (
            !!state.generatedData.originalValue[key] &&
            state.generatedData.originalValue[key].length > 2 &&
            allowed_languages.includes(key) &&
            key !== (state.generatedData?.selectedLocale === "nn" ? "no" : state.generatedData?.selectedLocale)
          ) {
            textToTranslate = state.generatedData.originalValue[key];
            break;
          } else {
            continue;
          }
        }
      }
    }
    try {
      if (!!textToTranslate) {
        const apiRequest = await Api.postFetchTranslatedAltText({
          altText: textToTranslate,
          locale: state?.generatedData?.selectedLocale ?? selectedUserLanguage,
        }).fetchDirect({});

        if (!!apiRequest?.content?.alt) {
          setGeneratedData({
            ...state.generatedData,
            loading: false,
            generatedResponse: apiRequest.content,
            doGenerate: false,
          });
        } else {
          if (!!apiRequest?.content?.error) {
            messenger.error(t("aiFunctions.couldNotGenerateAltTextFromText"), autoCloseDuration);
          } else {
            messenger.error(t("aiFunctions.failedToGenerateAltText"), autoCloseDuration);
          }
          setGeneratedData({ ...state.generatedData, loading: false, doGenerate: false });
        }
      } else {
        if (!state.generatedData?.imageSrc) {
          messenger.error(t("aiFunctions.noImageSourceFound"), autoCloseDuration);
          setGeneratedData({ ...state.generatedData, loading: false, doGenerate: false });
          return;
        }
        const apiRequest = await Api.postFetchImageAltText({
          src: state.generatedData.imageSrc,
          locale: state?.generatedData?.selectedLocale ?? selectedUserLanguage,
        }).fetchDirect({});

        if (!!apiRequest?.content?.alt) {
          setGeneratedData({
            ...state.generatedData,
            loading: false,
            generatedResponse: apiRequest.content,
            doGenerate: false,
          });
        } else {
          if (!!apiRequest?.content?.error) {
            messenger.error(t("aiFunctions.couldNotGenerateAltTextFromImage"), autoCloseDuration);
          } else {
            messenger.error(t("aiFunctions.failedToGenerateAltText"), autoCloseDuration);
          }
          setGeneratedData({ ...state.generatedData, loading: false, doGenerate: false });
        }
      }
    } catch (error) {
      messenger.error(t("aiFunctions.failedToGenerateAltText"), autoCloseDuration);
      setGeneratedData(null);
    }
  }

  useEffect(() => {
    if (
      state.generatedData?.doGenerate &&
      (state.generatedData.fieldId !== previousFieldId.current ||
        state.generatedData?.documentId !== previousDocumentId.current ||
        state.generatedData.generatedResponse === null ||
        state.generatedData.imageSrc !== previousImageSrc.current)
    ) {
      generateText();
      previousFieldId.current = state.generatedData.fieldId || null;
      previousImageSrc.current = state.generatedData.imageSrc || null;
      previousDocumentId.current = state.generatedData?.documentId || null;
    } else {
      setGeneratedData({ ...state.generatedData, doGenerate: false, loading: false });
    }
  }, [
    state.generatedData?.doGenerate,
    state.generatedData?.fieldId,
    state.generatedData?.generatedResponse,
    state.generatedData?.imageSrc,
  ]);
};
